import React, { useEffect, useState } from 'react';
import useTrimStore from '../../store/trimStore';
import { handleApiError } from '../../utils/errorHandler';

import TreeView from '../TreeView/TreeView';
import SelectedColumnsTree from '../SelectedColumnsTree/SelectedColumnsTree';

import * as S from './TrimModal.style';


const TrimModal = ({
  availableFiles = []
}) => {
  const {
    // 상태
    selectedFile,
    columns,
    selectedColumns,
    selectAll,
    outputFileName,
    previewData,
    isAnalyzing,
    isLoadingPreview,
    isGenerating,
    analysisError,
    previewError,
    generationError,
    treeData,
    terminalNodes,
    selectedTreeData,
    finalSelectedNodes,
    
    // 액션
    setSelectedFile,
    setAvailableFiles,
    setOutputFileName,
    analyzeFile,
    toggleColumn,
    toggleSelectAll,
    generatePreview,
    executeTrim,
    clearErrors,
    clearTreeStates
  } = useTrimStore();

    const [headerRows, setHeaderRows] = useState(6); // 기본값 6


  // 컴포넌트 마운트 시 파일 목록 설정
  useEffect(() => {
    setAvailableFiles(availableFiles);
  }, [availableFiles, setAvailableFiles]);

  // 에러 메시지 표시 및 자동 클리어
  useEffect(() => {
    if (analysisError || previewError || generationError) {
      const timer = setTimeout(() => {
        clearErrors();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [analysisError, previewError, generationError, clearErrors]);

  // 🔥 디버깅용 useEffect 추가
  useEffect(() => {
    console.log('🔥 [DEBUG] TrimModal - treeData 변경:', treeData);
  }, [treeData]);

  useEffect(() => {
    console.log('🔥 [DEBUG] TrimModal - terminalNodes 변경:', terminalNodes);
  }, [terminalNodes]);

  useEffect(() => {
    console.log('🔥 [DEBUG] TrimModal - selectedTreeData 변경:', selectedTreeData);
  }, [selectedTreeData]);

  const handleFileSelect = (event) => {
    const fileName = event.target.value;
    console.log('[TRIM_MODAL]', 'File selected:', fileName);
    setSelectedFile(fileName);
  };

const handleHeaderRowsChange = (event) => {
  const inputValue = event.target.value;
  
  // 🔥 공백이면 기본값 6 사용
  if (inputValue === '' || inputValue.trim() === '') {
    setHeaderRows(6);
    return;
  }
  
  // 🔥 1-20 범위 내에서만 허용
  const numValue = parseInt(inputValue, 10);
  if (!isNaN(numValue) && numValue >= 1 && numValue <= 20) {
    setHeaderRows(numValue);
  }
};

const handleAnalyze = async () => {
  if (!selectedFile) {
    alert('파일을 먼저 선택해주세요.');
    return;
  }

  console.log('[TRIM_MODAL]', 'Starting analysis for:', selectedFile);
  
  // 🆕 분석 시작 전 트리 관련 상태 즉시 초기화
  clearTreeStates();
  
  console.log('[TRIM_MODAL]', '🔥 트리 상태 초기화 완료');
  
  // 선택된 파일의 ID 찾기
  const targetFile = availableFiles.find(file => file.name === selectedFile);
  if (!targetFile) {
    alert('선택된 파일을 찾을 수 없습니다.');
    return;
  }

  const result = await analyzeFile(targetFile.id, headerRows);
  
  if (!result.success) {
    alert(`분석 실패: ${result.error}`);
  }
};

  const handleColumnToggle = (columnId) => {
    console.log('[TRIM_MODAL]', 'Column toggled:', columnId);
    toggleColumn(columnId);
  };

  const handleSelectAllToggle = () => {
    console.log('[TRIM_MODAL]', 'Select all toggled');
    toggleSelectAll();
  };

  const handleFileNameChange = (event) => {
    setOutputFileName(event.target.value);
  };

const handlePreviewGenerate = async () => {
  if (!selectedFile) {
    alert('파일을 먼저 선택해주세요.');
    return;
  }

  // 🔥 수정: finalSelectedNodes 우선 사용, 없으면 기존 selectedColumns 사용
  const columnsToUse = finalSelectedNodes.length > 0 ? finalSelectedNodes : selectedColumns;
  
  if (columnsToUse.length === 0) {
    alert('분석할 컬럼을 선택해주세요.');
    return;
  }

  const targetFile = availableFiles.find(file => file.name === selectedFile);
  if (!targetFile) {
    alert('선택된 파일을 찾을 수 없습니다.');
    return;
  }

  console.log('[TRIM_MODAL]', 'Generating preview for', columnsToUse.length, 'columns');
  console.log('[TRIM_MODAL]', 'Using finalSelectedNodes:', columnsToUse);
  
  // 🔥 수정: finalSelectedNodes를 서버가 기대하는 selected_columns 형식으로 변환
  const selectedColumnsForServer = columnsToUse.map(node => {
    console.log('[DEBUG] Converting node:', node);
    
    return {
      id: `col_${node.col}_${node.row}`,
      // 🔥 핵심 수정: 서버가 기대하는 필드명 사용
      name: node.originalName || node.name,           // 원본 이름
      path: node.originalPath || node.path || [],     // 원본 전체 경로 (매칭용)
      originalPath: node.originalPath || node.path || [], // 명시적으로 originalPath 전달
      displayPath: node.displayPath || [],            // 표시용 경로
      displayName: node.displayName || node.name,     // 표시용 이름
      col: node.col,
      row: node.row,
      selected: true
    };
  });

  console.log('[DEBUG] Converted selectedColumnsForServer:', selectedColumnsForServer);

  // 🔥 수정: selectedColumnsForServer를 두 번째 파라미터로 전달
  const result = await generatePreview(targetFile.id, selectedColumnsForServer, headerRows);
  
  if (!result.success) {
    alert(`미리보기 생성 실패: ${result.error}`);
  }
};

const handleExecuteTrim = async () => {
  if (!selectedFile) {
    alert('파일을 먼저 선택해주세요.');
    return;
  }

  // 🔥 수정: finalSelectedNodes 우선 사용, 없으면 기존 selectedColumns 사용
  const columnsToUse = finalSelectedNodes.length > 0 ? finalSelectedNodes : selectedColumns;
  
  if (columnsToUse.length === 0) {
    alert('선택할 컬럼이 없습니다.');
    return;
  }

  if (!outputFileName.trim()) {
    alert('파일명을 입력해주세요.');
    return;
  }

  const targetFile = availableFiles.find(file => file.name === selectedFile);
  if (!targetFile) {
    alert('선택된 파일을 찾을 수 없습니다.');
    return;
  }

  console.log('[TRIM_MODAL]', 'Executing trim with finalSelectedNodes:', columnsToUse);
  
  // 🔥 수정: finalSelectedNodes를 서버가 기대하는 selected_columns 형식으로 변환
  const selectedColumnsForServer = columnsToUse.map(node => ({
    id: `col_${node.col}_${node.row}`,
    name: node.originalName || node.name,           // 원본 이름
    path: node.originalPath || node.path || [],     // 원본 전체 경로 (매칭용)
    originalPath: node.originalPath || node.path || [], // 명시적으로 originalPath 전달
    displayPath: node.displayPath || [],            // 표시용 경로
    displayName: node.displayName || node.name,     // 표시용 이름
    col: node.col,
    row: node.row,
    selected: true
  }));

  const result = await executeTrim(targetFile.id, selectedColumnsForServer, outputFileName, headerRows);

  if (result.success) {
    alert(`파일이 성공적으로 생성되었습니다: ${result.data.filename}`);
    
    // 🔥 추가: 자동 파일 다운로드
    if (result.data.download_url) {
      downloadFile(result.data.download_url, result.data.filename);
    }
  } else {
    alert(`파일 생성 실패: ${result.error}`);
  }
};

// 🔥 추가: 파일 다운로드 함수
const downloadFile = (downloadUrl, filename) => {
  try {
    // JWT 토큰 가져오기
    const authStorage = localStorage.getItem('auth-storage');
    let token = '';
    
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage);
        token = state.token;
      } catch (error) {
        console.warn('[DOWNLOAD] Token parsing error:', error);
      }
    }
    
    // API 기본 URL 구성
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pivo-api.fly.dev';
    const fullUrl = `${API_BASE_URL}${downloadUrl}`;
    
    // 숨겨진 링크 생성하여 다운로드 트리거
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = filename;
    
    // Authorization 헤더는 링크 다운로드에서 사용할 수 없으므로
    // fetch를 사용하여 blob으로 다운로드
    fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    })
    .then(blob => {
      // Blob URL 생성
      const blobUrl = window.URL.createObjectURL(blob);
      
      // 다운로드 링크 생성 및 클릭
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      // 정리
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('[DOWNLOAD]', 'File downloaded successfully:', filename);
    })
    .catch(error => {
      console.error('[DOWNLOAD]', 'Download error:', error);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    });
    
  } catch (error) {
    console.error('[DOWNLOAD]', 'Download preparation error:', error);
    alert('파일 다운로드 준비 중 오류가 발생했습니다.');
  }
};

  return (
    <S.ModalContainer>
      <S.SectionTitle>데이터 선택하기</S.SectionTitle>
      
      <S.FileSelectGroup>
        <S.FileSelectLabel>데이터 선택하기</S.FileSelectLabel>
        <S.FileSelectWrapper>
          <S.FileSelect 
            value={selectedFile} 
            onChange={handleFileSelect}
            hasValue={!!selectedFile}
            disabled={isAnalyzing}
          >
            <option value="">Select a file</option>
            {availableFiles.map(file => (
              <option key={file.id} value={file.name}>
                {file.name}
              </option>
            ))}
          </S.FileSelect>
        </S.FileSelectWrapper>
      </S.FileSelectGroup>

      <S.HeaderRowsGroup>
        <S.HeaderRowsLabel># Header(변수의 행 수)</S.HeaderRowsLabel>
        <S.HeaderRowsWrapper>
          <S.HeaderRowsInput
            type="number"
            value={headerRows}
            onChange={handleHeaderRowsChange}
            min="1"
            max="20"
            disabled={isAnalyzing}
          />
          {/* <S.HeaderRowsHint>
            💡 Excel 파일에서 헤더로 사용할 행의 개수를 입력하세요 (기본값: 6)
          </S.HeaderRowsHint> */}
        </S.HeaderRowsWrapper>
      </S.HeaderRowsGroup>

      <S.ColumnSelectGroup>
        <S.ColumnSelectHeader>
          <S.ColumnSelectLabel>선택된 파일</S.ColumnSelectLabel>
          <S.AnalyzeButton 
            onClick={handleAnalyze}
            disabled={!selectedFile || isAnalyzing}
          >
            {isAnalyzing ? '분석중...' : '구조 확인하기'}
          </S.AnalyzeButton>
        </S.ColumnSelectHeader>

        <S.FileStatusBox hasFile={!!selectedFile}>
          {selectedFile ? (
            <S.FileStatusText>파일이 선택되었습니다: {selectedFile}</S.FileStatusText>
          ) : (
            <S.FileStatusText>입력된 파일이 없습니다</S.FileStatusText>
          )}
        </S.FileStatusBox>

        {/* 에러 메시지 표시 */}
        {analysisError && (
          <S.ErrorMessage>
            분석 오류: {analysisError}
          </S.ErrorMessage>
        )}
      </S.ColumnSelectGroup>

      <S.ColumnConfigGroup>
        <S.ColumnConfigTitle>변수 선택하기</S.ColumnConfigTitle>
        <S.ColumnConfigSubTitle> 
          새로운 데이터셋으로 구성하고자 하는 변수를 선택하세요.
          </S.ColumnConfigSubTitle>

        <S.ColumnConfigDescription>
          <S.DescriptionIcon></S.DescriptionIcon>
          <div>
            <strong>Tree View 사용법:</strong><br/>
            체크박스를 클릭하여 원하는 변수를 선택하세요. <strong>열기/닫기</strong> 버튼으로 하위 항목을 펼치거나 접을 수 있습니다.<br/>
            <S.ExampleText>
              💡 팁: 상위 카테고리를 먼저 펼쳐서 세부 변수들을 확인한 후 필요한 항목만 체크하세요.
            </S.ExampleText>
          </div>
        </S.ColumnConfigDescription>

        <S.ColumnConfigBox>
          {/* 🆕 트리 뷰 추가 */}
          {isAnalyzing ? (
            <S.LoadingMessage>
              <S.LoadingSpinner />
              <S.LoadingText>파일을 분석하고 있습니다...</S.LoadingText>
            </S.LoadingMessage>
          ) : treeData ? (
            <>
              <TreeView />
              {/* 🔥 디버깅용 정보 표시 */}
              {/* <div style={{background: '#f0f0f0', padding: '10px', margin: '10px 0'}}>
                <strong>디버깅 정보:</strong><br/>
                terminalNodes 개수: {terminalNodes.length}<br/>
                selectedTreeData 존재: {selectedTreeData ? 'Yes' : 'No'}<br/>
                selectedTreeData children: {selectedTreeData?.children?.length || 0}
              </div> */}
            </>
          ) : (
            <S.EmptyColumnsMessage>
              파일을 선택하고 분석 버튼을 클릭하세요
            </S.EmptyColumnsMessage>
          )}
          
          {/* 🆕 선택된 터미널 노드 표시 */}
          {/* {terminalNodes.length > 0 && (
            <S.SelectedNodesSection>
              <S.SelectedNodesTitle>선택된 컬럼들:</S.SelectedNodesTitle>
              {terminalNodes.map((node, index) => (
                <S.SelectedNodeItem key={index}>
                  {node.path ? node.path.join(' → ') : node.name}
                </S.SelectedNodeItem>
              ))}
            </S.SelectedNodesSection>
          )} */}
        </S.ColumnConfigBox>
      </S.ColumnConfigGroup>

      {console.log('🔥 [DEBUG] 조건 체크 - selectedTreeData:', selectedTreeData)}
      {console.log('🔥 [DEBUG] 조건 체크 - children 존재:', selectedTreeData?.children)}
      {console.log('🔥 [DEBUG] 조건 체크 - children 길이:', selectedTreeData?.children?.length)}

      {/* {!isAnalyzing && selectedTreeData && selectedTreeData.children && selectedTreeData.children.length > 0 && (
        <S.ColumnConfigGroup>
          <S.ColumnConfigTitle>컬럼명 지정하기</S.ColumnConfigTitle>
          <S.ColumnConfigBox>
            <SelectedColumnsTree />
            
            {finalSelectedNodes.length > 0 && (
              <S.SelectedNodesSection>
                <S.SelectedNodesTitle>최종 선택된 컬럼들:</S.SelectedNodesTitle>
                {finalSelectedNodes.map((node, index) => (
                  <S.SelectedNodeItem key={index}>
                    {node.path ? node.path.join(' → ') : node.name}
                  </S.SelectedNodeItem>
                ))}
              </S.SelectedNodesSection>
            )}
          </S.ColumnConfigBox>
        </S.ColumnConfigGroup>
      )} */}

{!isAnalyzing && selectedTreeData && selectedTreeData.children && selectedTreeData.children.length > 0 && (
  <S.ColumnConfigGroup>
    <S.ColumnConfigTitle>
      변수명 지정하기
      {finalSelectedNodes.length > 0 && (
        <S.ColumnCount>({finalSelectedNodes.length}개 변수)</S.ColumnCount>
      )}
    </S.ColumnConfigTitle>

    <S.ColumnConfigSubTitle>
      변수명에 포함되는 상위 경로 포함여부를 선택할 수 있습니다.
    </S.ColumnConfigSubTitle>
    
    <S.ColumnConfigDescription>
      <S.DescriptionIcon>📝</S.DescriptionIcon>
      <div>
        <strong>변수명 구성 방법:</strong><br/>
        아래 트리에서 체크한 노드들만으로 최종 변수명이 생성됩니다.<br/>
        <S.ExampleText>
          예: "I. 병원 현황 → 병원형태 → 설립주체"에서 "병원형태 ✓", "설립주체 ✓"만 체크하면<br/>
          → 최종 컬럼명: "<strong>병원형태 → 설립주체</strong>"
        </S.ExampleText>
      </div>
    </S.ColumnConfigDescription>
    
    <S.ColumnConfigBox>
      <SelectedColumnsTree />
      
      {finalSelectedNodes.length > 0 && (
        <S.SelectedNodesSection>
          <S.SectionHeader>
            <S.SelectedNodesTitle>
              <S.PreviewIcon>👀</S.PreviewIcon>
              컬럼명 미리보기
            </S.SelectedNodesTitle>
            <S.ModificationStats>
              {finalSelectedNodes.filter(node => 
                node.displayPath && node.originalPath && 
                node.displayPath.join('→') !== node.originalPath.join('→')
              ).length}개 컬럼명 수정됨
            </S.ModificationStats>
          </S.SectionHeader>
          
          <S.ColumnPreviewTable>
            <S.ColumnPreviewHeader>
              <S.ColumnPreviewHeaderCell>원본 경로</S.ColumnPreviewHeaderCell>
              <S.ArrowHeaderCell>→</S.ArrowHeaderCell>
              <S.ColumnPreviewHeaderCell>최종 컬럼명</S.ColumnPreviewHeaderCell>
              <S.StatusHeaderCell>상태</S.StatusHeaderCell>
            </S.ColumnPreviewHeader>
            <S.ColumnPreviewBody>
              {finalSelectedNodes.map((node, index) => {
                // 🔥 핵심: originalPath와 displayPath 구분 처리
                const originalPath = node.originalPath ? 
                  node.originalPath.join(' → ') : 
                  (node.path?.join(' → ') || node.name);
                
                const displayPath = node.displayPath ? 
                  node.displayPath.join(' → ') : 
                  (node.displayName || originalPath);
                
                const isModified = node.displayPath && node.originalPath && 
                                 node.displayPath.join('→') !== node.originalPath.join('→');
                
                return (
                  <S.ColumnPreviewRow key={index} modified={isModified}>
                    <S.ColumnPreviewCell>
                      <S.PathText truncated title={originalPath}>
                        {originalPath}
                      </S.PathText>
                    </S.ColumnPreviewCell>
                    <S.ArrowCell>
                      {isModified ? '✂️' : '→'}
                    </S.ArrowCell>
                    <S.ColumnPreviewCell highlighted>
                      <S.PathText>
                        <strong>{displayPath}</strong>
                      </S.PathText>
                    </S.ColumnPreviewCell>
                    <S.StatusCell>
                      {isModified ? (
                        <S.StatusBadge modified>수정됨</S.StatusBadge>
                      ) : (
                        <S.StatusBadge>원본</S.StatusBadge>
                      )}
                    </S.StatusCell>
                  </S.ColumnPreviewRow>
                );
              })}
            </S.ColumnPreviewBody>
          </S.ColumnPreviewTable>
          
          {/* <S.ColumnNamingTip>
            <S.TipIcon>💡</S.TipIcon>
            <div>
              <strong>팁:</strong> 위 트리에서 체크박스를 해제하면 해당 노드가 컬럼명에서 제외됩니다. 
              모든 노드를 해제하면 원본 전체 경로가 사용됩니다.
            </div>
          </S.ColumnNamingTip> */}
        </S.SelectedNodesSection>
      )}
    </S.ColumnConfigBox>
  </S.ColumnConfigGroup>
)}


      <S.FileNameGroup>
        <S.FileNameLabel>파일명 지정하기</S.FileNameLabel>
        <S.FileNameSubLabel>
          선택한 변수로 구성된 데이터셋의 파일명 지정
        </S.FileNameSubLabel>
        <S.FileNameInput 
          type="text"
          value={outputFileName}
          onChange={handleFileNameChange}
          placeholder="selected_data"
          disabled={isGenerating}
        />
      </S.FileNameGroup>

      <S.PreviewGroup>
        <S.PreviewHeader>
          <S.PreviewLabel>미리보기 / 내보내기</S.PreviewLabel>
          <S.ButtonsWrapper>
            <S.PreviewButton 
              onClick={handlePreviewGenerate}
              disabled={isAnalyzing || finalSelectedNodes.length === 0 || isLoadingPreview}
            >
              {isLoadingPreview ? '생성중...' : '미리보기'}
            </S.PreviewButton>
            <S.ExecuteButton 
              onClick={handleExecuteTrim}
              disabled={isAnalyzing || finalSelectedNodes.length === 0 || !outputFileName.trim() || isGenerating}
            >
              {isGenerating ? '내보내는 중...' : '내보내기'}
            </S.ExecuteButton>
          </S.ButtonsWrapper>
        </S.PreviewHeader>

      {generationError && (
        <S.ErrorMessage>
          파일 생성 오류: {generationError}
        </S.ErrorMessage>
      )}
        
        <S.PreviewBox>
          {previewError && (
            <S.ErrorMessage>
              미리보기 오류: {previewError}
            </S.ErrorMessage>
          )}
          
          {previewData ? (
            <S.PreviewTable>
              <S.PreviewTableHeader>
                <tr>
                  {previewData.columns.map((col, index) => (
                    <S.PreviewTableHeaderCell key={index}>
                      {col}
                    </S.PreviewTableHeaderCell>
                  ))}
                </tr>
              </S.PreviewTableHeader>
              <S.PreviewTableBody>
                {previewData.data.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <S.PreviewTableCell key={cellIndex}>
                        {cell}
                      </S.PreviewTableCell>
                    ))}
                  </tr>
                ))}
              </S.PreviewTableBody>
            </S.PreviewTable>
          ) : (
            <S.PreviewEmptyState>
              {isLoadingPreview ? '미리보기 생성 중...' : '컬럼을 선택하고 미리보기 버튼을 클릭하세요'}
            </S.PreviewEmptyState>
          )}
        </S.PreviewBox>
      </S.PreviewGroup>


    </S.ModalContainer>
  );
};

export default TrimModal;